import { Suspense, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { NotFound } from "./pages/NotFound";
import { CRITICAL_IMAGES } from "./lib/utils";
import { ScrollToTop } from "./components/ScrollToTop";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import {
  lazyWithPreload,
  registerRouteComponents,
  preloadRouteComponents,
} from "./utils/lazyUtils";

// Page components
const Landing = lazyWithPreload(() =>
  import("./pages/Landing").then((module) => ({ default: module.Landing })),
);
const Solutions = lazyWithPreload(() =>
  import("./pages/Solutions").then((module) => ({ default: module.Solutions })),
);
const Safety = lazyWithPreload(() =>
  import("./pages/Safety").then((module) => ({ default: module.Safety })),
);
const About = lazyWithPreload(() =>
  import("./pages/About").then((module) => ({ default: module.About })),
);
const Contact = lazyWithPreload(() =>
  import("./pages/Contact").then((module) => ({ default: module.Contact })),
);
const TermsAndConditions = lazyWithPreload(() =>
  import("./pages/TermsAndConditions").then((module) => ({
    default: module.TermsAndConditions,
  })),
);
const Pricing = lazyWithPreload(() =>
  import("./pages/Pricing").then((module) => ({ default: module.Pricing })),
);
const Careers = lazyWithPreload(() =>
  import("./pages/Careers").then((module) => ({ default: module.Careers })),
);
const Blog = lazyWithPreload(() =>
  import("./pages/Blog").then((module) => ({ default: module.Blog })),
);
const News = lazyWithPreload(() =>
  import("./pages/News").then((module) => ({ default: module.News })),
);

// Career subpages
const ApplicationForm = lazyWithPreload(() =>
  import("./pages/careers/applicationform").then((module) => ({
    default: module.ApplicationForm,
  })),
);

// Blog subpages
const BlogPages = lazyWithPreload(() =>
  import("./pages/blog/post-list").then((module) => ({
    default: module.default,
  })),
);
const PostPage = lazyWithPreload(() =>
  import("./pages/blog/post/page").then((module) => ({
    default: module.default,
  })),
);

// Blog subpages
const NewsPages = lazyWithPreload(() =>
  import("./pages/news/article-list").then((module) => ({
    default: module.default,
  })),
);
const ArticlePage = lazyWithPreload(() =>
  import("./pages/news/articles/page").then((module) => ({
    default: module.default,
  })),
);

// Solution subpages
const Chiropractors = lazyWithPreload(() =>
  import("./pages/solutions/Chiropractors").then((module) => ({
    default: module.Chiropractors,
  })),
);
const Fitness = lazyWithPreload(() =>
  import("./pages/solutions/Fitness").then((module) => ({
    default: module.Fitness,
  })),
);
const Supplements = lazyWithPreload(() =>
  import("./pages/solutions/Supplements").then((module) => ({
    default: module.Supplements,
  })),
);
const Clinics = lazyWithPreload(() =>
  import("./pages/solutions/Clinics").then((module) => ({
    default: module.Clinics,
  })),
);
const Entrepreneurs = lazyWithPreload(() =>
  import("./pages/solutions/Entrepreneurs").then((module) => ({
    default: module.Entrepreneurs,
  })),
);

// Register components for route-based preloading
registerRouteComponents("/", [Landing]);
registerRouteComponents("/solutions", [Solutions]);
registerRouteComponents("/safety", [Safety]);
registerRouteComponents("/about", [About]);
registerRouteComponents("/contact", [Contact]);
registerRouteComponents("/terms-and-conditions", [TermsAndConditions]);
registerRouteComponents("/pricing", [Pricing]);
registerRouteComponents("/careers", [Careers]);
registerRouteComponents("/careers/applicationform", [ApplicationForm]);
registerRouteComponents("/blog", [Blog]);
registerRouteComponents("/blog/post-list", [BlogPages]);
registerRouteComponents("/blog/:slug", [PostPage]);
registerRouteComponents("/news", [News]);
registerRouteComponents("/news/article-list", [NewsPages]);
registerRouteComponents("/news/:slug", [ArticlePage]);
registerRouteComponents("/solutions/chiropractors", [Chiropractors]);
registerRouteComponents("/solutions/fitness", [Fitness]);
registerRouteComponents("/solutions/supplements", [Supplements]);
registerRouteComponents("/solutions/clinics", [Clinics]);
registerRouteComponents("/solutions/entrepreneurs", [Entrepreneurs]);

// Loading Components
const LoadingMessage = ({ message = "Loading..." }) => (
  <div className="min-h-screen w-full bg-white flex items-center justify-center">
    <div className="text-center max-w-md mx-auto px-4">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-[#0F5894] mb-2">
          {message}
        </h2>
        <p className="text-gray-600">Powering scalable wellness solutions...</p>
      </div>
      <div className="w-full h-16 mb-4 flex items-center justify-center">
        <svg width="200" height="60" viewBox="0 0 200 60" className="ecg-wave">
          <path
            d="M0,30 L40,30 L45,10 L50,50 L55,30 L60,30 L65,25 L70,35 L75,30 L200,30"
            stroke="#0F5894"
            strokeWidth="2"
            fill="none"
            className="ecg-path"
          />
        </svg>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .ecg-wave {
            animation: ecgPulse 2s ease-in-out infinite;
          }
          .ecg-path {
            stroke-dasharray: 200;
            stroke-dashoffset: 200;
            animation: ecgDraw 2s ease-in-out infinite;
          }
          @keyframes ecgPulse {
            0%, 100% { opacity: 0.7; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.05); }
          }
          @keyframes ecgDraw {
            0% { stroke-dashoffset: 200; }
            50% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -200; }
          }
        `,
        }}
      />
    </div>
  </div>
);

const SectionLoading = ({ height = "h-96" }) => (
  <div
    className={`w-full ${height} bg-gray-50 flex items-center justify-center`}
  >
    <div className="text-center">
      <div className="w-32 h-8 mb-2 flex items-center justify-center">
        <svg
          width="120"
          height="30"
          viewBox="0 0 120 30"
          className="ecg-wave-small"
        >
          <path
            d="M0,15 L25,15 L28,8 L32,22 L35,15 L40,15 L42,12 L45,18 L48,15 L120,15"
            stroke="#0F5894"
            strokeWidth="1.5"
            fill="none"
            className="ecg-path-small"
          />
        </svg>
      </div>
      <p className="text-sm text-gray-500">Loading section...</p>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .ecg-wave-small {
            animation: ecgPulseSmall 1.5s ease-in-out infinite;
          }
          .ecg-path-small {
            stroke-dasharray: 120;
            stroke-dashoffset: 120;
            animation: ecgDrawSmall 1.5s ease-in-out infinite;
          }
          @keyframes ecgPulseSmall {
            0%, 100% { opacity: 0.6; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.1); }
          }
          @keyframes ecgDrawSmall {
            0% { stroke-dashoffset: 120; }
            50% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -120; }
          }
        `,
        }}
      />
    </div>
  </div>
);

// Preload critical components on app initialization
const preloadCriticalComponents = () => {
  // Preload Landing page immediately
  Landing.preload();
};

// Preload components on mouse enter for navigation links
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const handleNavHover = (route: string) => {
  // Convert route name to full path for consistency
  const routePath = route === "home" ? "/" : `/${route}`;
  preloadRouteComponents(routePath);
};
// Initialize preloading
if (typeof window !== "undefined") {
  // Preload on page load
  window.addEventListener("load", preloadCriticalComponents);

  // Preload on user interaction (more aggressive)
  const events = ["mousedown", "touchstart"];
  events.forEach((event) => {
    window.addEventListener(event, preloadCriticalComponents, { once: true });
  });
}

export function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(
    "Connecting to healthcare ecosystem...",
  );

  useEffect(() => {
    let fontsLoaded = false;
    let logoLoaded = false;
    let componentsReady = false;

    const checkAllResourcesLoaded = () => {
      if (fontsLoaded && logoLoaded && componentsReady) {
        setLoadingMessage("Launching healthcare platform...");
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }
    };

    // Priority 1: Load critical fonts
    setLoadingMessage("Loading enterprise platform...");

    // Check if fonts are loaded (simplified with preloading)
    const checkFonts = () => {
      if (document.fonts) {
        // Since font is preloaded in HTML, wait for document.fonts.ready
        document.fonts.ready
          .then(() => {
            fontsLoaded = true;
            setLoadingMessage("Initializing business solutions...");
            checkAllResourcesLoaded();
          })
          .catch(() => {
            // Fallback if font loading fails
            fontsLoaded = true;
            setLoadingMessage("Initializing business solutions...");
            checkAllResourcesLoaded();
          });
      } else {
        // Fallback for browsers without font loading API
        setTimeout(() => {
          fontsLoaded = true;
          setLoadingMessage("Initializing business solutions...");
          checkAllResourcesLoaded();
        }, 800);
      }
    };

    // Priority 2: Preload logo and critical images
    const preloadCriticalAssets = () => {
      const criticalImages = [...CRITICAL_IMAGES];

      let loadedCount = 0;
      const totalImages = criticalImages.length;

      criticalImages.forEach((src) => {
        const img = new Image();
        img.onload = () => {
          loadedCount++;
          if (loadedCount >= 1 && !logoLoaded) {
            // At least one logo loads
            logoLoaded = true;
            setLoadingMessage("Configuring compliance systems...");
            checkAllResourcesLoaded();
          }
        };
        img.onerror = () => {
          loadedCount++;
          if (loadedCount >= totalImages && !logoLoaded) {
            logoLoaded = true; // Continue even if logo fails
            setLoadingMessage("Configuring compliance systems...");
            checkAllResourcesLoaded();
          }
        };
        img.src = src;
      });

      // Fallback timeout for logo loading
      setTimeout(() => {
        if (!logoLoaded) {
          logoLoaded = true;
          setLoadingMessage("Configuring compliance systems...");
          checkAllResourcesLoaded();
        }
      }, 2000);
    };

    // Priority 3: Wait for critical components and page load
    const handleComponentsReady = () => {
      // Components are ready, no need to preload Hero again as it's already preloaded
      setTimeout(() => {
        componentsReady = true;
        setLoadingMessage("Establishing secure connections...");
        checkAllResourcesLoaded();
      }, 800);
    };

    // Start loading sequence
    checkFonts();
    setTimeout(preloadCriticalAssets, 200);
    setTimeout(handleComponentsReady, 500);

    // Also ensure page is fully loaded
    const handlePageLoad = () => {
      setTimeout(() => {
        if (!componentsReady) {
          componentsReady = true;
          checkAllResourcesLoaded();
        }
      }, 100);
    };

    if (document.readyState === "complete") {
      handlePageLoad();
    } else {
      window.addEventListener("load", handlePageLoad);
      return () => window.removeEventListener("load", handlePageLoad);
    }
  }, []);

  // Show loading screen until everything is ready
  if (isLoading) {
    return <LoadingMessage message={loadingMessage} />;
  }

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <GoogleAnalytics />
        <Suspense fallback={<LoadingMessage />}>
          <Routes>
            <Route
              path="/"
              element={
                <Suspense
                  fallback={<LoadingMessage message="Loading Axentra Health" />}
                >
                  <Landing />
                </Suspense>
              }
            />
            <Route
              path="/solutions"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Solutions />
                </Suspense>
              }
            />
            <Route
              path="/solutions/chiropractors"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Chiropractors />
                </Suspense>
              }
            />
            <Route
              path="/solutions/fitness"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Fitness />
                </Suspense>
              }
            />
            <Route
              path="/solutions/supplements"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Supplements />
                </Suspense>
              }
            />
            <Route
              path="/solutions/clinics"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Clinics />
                </Suspense>
              }
            />
            <Route
              path="/solutions/entrepreneurs"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Entrepreneurs />
                </Suspense>
              }
            />
            <Route
              path="/safety"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Safety />
                </Suspense>
              }
            />
            <Route
              path="/about"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <About />
                </Suspense>
              }
            />
            <Route
              path="/contact"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Contact />
                </Suspense>
              }
            />
            <Route
              path="/pricing"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Pricing />
                </Suspense>
              }
            />
            <Route
              path="/careers"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Careers />
                </Suspense>
              }
            />
            <Route
              path="/careers/applicationform"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <ApplicationForm />
                </Suspense>
              }
            />
            <Route
              path="/blog"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <Blog />
                </Suspense>
              }
            />
            <Route
              path="/news"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <News />
                </Suspense>
              }
            />
            <Route
              path="/terms-and-conditions"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <TermsAndConditions />
                </Suspense>
              }
            />
            <Route
              path="/blog/post-list"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <BlogPages />
                </Suspense>
              }
            />
            <Route
              path="/news/article-list"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <NewsPages />
                </Suspense>
              }
            />
            <Route
              path="/news/:slug"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <ArticlePage />
                </Suspense>
              }
            />
            <Route
              path="/blog/:slug"
              element={
                <Suspense fallback={<SectionLoading height="h-screen" />}>
                  <PostPage />
                </Suspense>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </HelmetProvider>
  );
}
